import { reactive } from 'vue';
import type { CardLook, CardUse } from '@vp/shared-types';
import { profileLocal } from './profiles/local';

/**
 * Profile tray and its panel library (docs/ru/MODULE-API.md §6.19, docs/TZ-PROFILES.md).
 *
 * The gray tray never grows past PROFILE_TRAY_MAX.height. Its width follows the
 * profile column; PROFILE_TRAY_MAX.width is the column the minimum card fraction
 * was measured against. Card position and size are fractions of that tray, so a future cloud sync
 * can ship the same record to another device. The catalog is the list the library
 * shows: the core cards, the cards of running modules (API 3.0, manifest
 * `contributes.profileCards`, kept in step by `setModuleProfilePanels`) and later a server feed.
 */

/** Height is a hard cap. Width is the column the minimum card fraction was measured on. */
export const PROFILE_TRAY_MAX = { width: 776, height: 509 };

/** `.stats` padding is 12px and its border is 1px. Cards live in what remains. */
const TRAY_CHROME = 12 * 2 + 1 * 2;

/** Inner width and height the fractions below are measured against. */
export const PROFILE_BOARD_MAX_W = PROFILE_TRAY_MAX.width - TRAY_CHROME;
export const PROFILE_BOARD_MAX_H = PROFILE_TRAY_MAX.height - TRAY_CHROME;

export const STAT_MIN_W = 120;
export const STAT_MIN_H = 84;
const STAT_GAP = 12;
const STAT_ROW = 108;

/** `profileLocal` key. Rides along in a profile export with the other keys. */
export const PROFILE_LAYOUT_KEY = 'vp-profile-stats';

export type PanelOrigin = 'core' | 'server' | 'module' | 'user';

/** One entry in the panel library. `id` is unique across the whole library. */
export interface ProfilePanel {
  id: string;
  origin: PanelOrigin;
  /** i18n key. Core cards use this. */
  titleKey?: string;
  /** Words supplied by a future server or module. */
  title?: { ru?: string; en?: string };
  /** Set when `origin` is `module`. */
  moduleId?: string;
  /** The card's id in the module's manifest (`origin: 'module'`) or of the person's card (`origin: 'user'`). */
  card?: string;
  /** The HTML of a card the person made (`origin: 'user'`, `user-cards.ts`). */
  html?: string;
  /** Size of a freshly added card, px of the tray (the library places it in the first free spot). */
  size?: { w: number; h: number };
  /** API 4.10: the module's look of the card and what the card uses of the platform. */
  look?: CardLook;
  uses?: CardUse[];
}

/**
 * One card on the tray. `x` and `w` are fractions of the tray's inner width,
 * `y` and `h` are fractions of its inner height cap. `shown: false` keeps the
 * place, so the library can put the card back where it was.
 */
export interface ProfileCardBox {
  x: number;
  y: number;
  w: number;
  h: number;
  shown: boolean;
}

/** The record a future sync sends. `pending` until the cloud copy matches it. */
export interface ProfileLayout {
  v: 1;
  sync: 'pending' | 'acked';
  /** Grows on every local edit. A future sync compares it with the cloud copy. */
  rev: number;
  cards: Record<string, ProfileCardBox>;
}

export interface StatPixels {
  x: number;
  y: number;
  w: number;
  h: number;
}

const CORE: ProfilePanel[] = [
  { id: 'canvases', origin: 'core', titleKey: 'profile.canvases' },
  { id: 'objects', origin: 'core', titleKey: 'profile.objects' },
  { id: 'popular', origin: 'core', titleKey: 'profile.popular' },
  { id: 'clock', origin: 'core', titleKey: 'profile.clock', size: { w: 220, h: STAT_ROW } },
];

const extras = reactive<ProfilePanel[]>([]);
let saved: ProfileLayout | null = null;

/** A future server response or module host adds a panel here. Duplicate ids are ignored. */
export function registerProfilePanel(panel: ProfilePanel): void {
  const id = panel.id.trim();
  if (!id || profilePanels().some((p) => p.id === id)) return;
  extras.push({ ...panel, id });
}

/**
 * API 3.0: the cards of running modules. Replaces every module card in the library with `panels`: a
 * card of a module that was stopped or removed leaves the library; its place on the tray is kept, so it
 * comes back where it was when the module returns.
 */
export function setModuleProfilePanels(panels: ProfilePanel[]): void {
  for (let i = extras.length - 1; i >= 0; i--) if (extras[i].origin === 'module') extras.splice(i, 1);
  for (const panel of panels) registerProfilePanel({ ...panel, origin: 'module' });
}

/** API 4.10: cards a module made with `show` wait here until the profile screen puts them on the tray. */
export const showRequests = reactive<string[]>([]);
export function requestShowPanel(id: string): void {
  if (!showRequests.includes(id)) showRequests.push(id);
}

/** The person's own cards (`user-cards.ts`): replaced as a whole on every change. */
export function setUserProfilePanels(panels: ProfilePanel[]): void {
  for (let i = extras.length - 1; i >= 0; i--) if (extras[i].origin === 'user') extras.splice(i, 1);
  for (const panel of panels) registerProfilePanel({ ...panel, origin: 'user' });
}

export function profilePanels(): ProfilePanel[] {
  return [...CORE, ...extras];
}

/** Panels the library offers: not on the tray right now. */
export function hiddenPanels(layout: ProfileLayout): ProfilePanel[] {
  return profilePanels().filter((p) => !layout.cards[p.id]?.shown);
}

/** Tests drop registered panels and the in-memory copy between cases. */
export function resetProfilePanelsForTests(): void {
  extras.length = 0;
  saved = null;
}

function clampFrac(card: ProfileCardBox): ProfileCardBox {
  const minW = STAT_MIN_W / PROFILE_BOARD_MAX_W;
  const minH = STAT_MIN_H / PROFILE_BOARD_MAX_H;
  const w = Math.min(1, Math.max(minW, card.w));
  const h = Math.min(1, Math.max(minH, card.h));
  return {
    x: Math.min(Math.max(0, card.x), Math.max(0, 1 - w)),
    y: Math.min(Math.max(0, card.y), Math.max(0, 1 - h)),
    w,
    h,
    shown: card.shown !== false,
  };
}

function clampPixels(px: StatPixels, boardW: number): StatPixels {
  const width = Math.max(boardW, 1);
  const w = Math.min(width, Math.max(Math.min(STAT_MIN_W, width), px.w));
  const h = Math.min(PROFILE_BOARD_MAX_H, Math.max(Math.min(STAT_MIN_H, PROFILE_BOARD_MAX_H), px.h));
  return {
    x: Math.min(Math.max(0, px.x), Math.max(0, width - w)),
    y: Math.min(Math.max(0, px.y), Math.max(0, PROFILE_BOARD_MAX_H - h)),
    w,
    h,
  };
}

/** Where a stored card sits inside the current tray. */
export function cardPixels(card: ProfileCardBox, boardW: number): StatPixels {
  const width = Math.max(boardW, 1);
  return clampPixels({ x: card.x * width, y: card.y * PROFILE_BOARD_MAX_H, w: card.w * width, h: card.h * PROFILE_BOARD_MAX_H }, width);
}

/** The inverse of `cardPixels`, clamped so the card cannot leave the tray. */
export function pixelsToCard(px: StatPixels, boardW: number, shown: boolean): ProfileCardBox {
  const width = Math.max(boardW, 1);
  const rect = clampPixels(px, width);
  return { x: rect.x / width, y: rect.y / PROFILE_BOARD_MAX_H, w: rect.w / width, h: rect.h / PROFILE_BOARD_MAX_H, shown };
}

function isBox(value: unknown): value is { x: number; y: number; w: number; h: number } {
  if (!value || typeof value !== 'object') return false;
  const box = value as { x: unknown; y: unknown; w: unknown; h: unknown };
  return [box.x, box.y, box.w, box.h].every((n) => typeof n === 'number' && Number.isFinite(n));
}

/** The layout from before fractions of height: x and w were fractions, y and h were pixels. */
function migrateLegacy(data: Record<string, unknown>): ProfileLayout {
  const cards: Record<string, ProfileCardBox> = {};
  for (const [id, raw] of Object.entries(data)) {
    if (!isBox(raw)) continue;
    cards[id] = clampFrac({ x: raw.x, y: raw.y / PROFILE_BOARD_MAX_H, w: raw.w, h: raw.h / PROFILE_BOARD_MAX_H, shown: true });
  }
  return { v: 1, sync: 'pending', rev: 1, cards };
}

function normalize(raw: Record<string, unknown>): ProfileLayout | null {
  if (!raw.cards || typeof raw.cards !== 'object') return null;
  const cards: Record<string, ProfileCardBox> = {};
  for (const [id, box] of Object.entries(raw.cards as Record<string, unknown>)) {
    if (!isBox(box)) continue;
    cards[id] = clampFrac({ x: box.x, y: box.y, w: box.w, h: box.h, shown: (box as { shown?: unknown }).shown !== false });
  }
  const rev = typeof raw.rev === 'number' && Number.isFinite(raw.rev) ? raw.rev : 0;
  return { v: 1, sync: raw.sync === 'pending' ? 'pending' : 'acked', rev, cards };
}

function parse(raw: unknown): ProfileLayout | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  if (data.v === 1) return normalize(data);
  const values = Object.values(data);
  if (values.length > 0 && values.every(isBox)) return migrateLegacy(data);
  return null;
}

/** Built-in cards: a row of counters, then the clock (an extra row when the tray is narrow). Not written until the person edits. */
export function defaultProfileLayout(boardW: number): ProfileLayout {
  const width = Math.max(boardW, 1);
  const cards: Record<string, ProfileCardBox> = {};
  const put = (id: string, x: number, y: number, w: number) => {
    cards[id] = pixelsToCard({ x, y, w, h: STAT_ROW }, width, true);
  };
  const row = STAT_ROW + STAT_GAP;
  if (width < 520) {
    const col = (width - STAT_GAP) / 2;
    put('canvases', 0, 0, col);
    put('objects', col + STAT_GAP, 0, col);
    put('popular', 0, row, width);
    put('clock', 0, row * 2, col);
  } else {
    const small = 148;
    put('canvases', 0, 0, small);
    put('objects', small + STAT_GAP, 0, small);
    const used = (small + STAT_GAP) * 2;
    put('popular', used, 0, Math.max(STAT_MIN_W, width - used));
    put('clock', 0, row, 220);
  }
  return { v: 1, sync: 'acked', rev: 0, cards };
}

/**
 * A layout saved before a core card existed gets that card in the first free spot.
 * A card the person hid (`shown: false`) stays hidden. The copy is not marked pending.
 */
export function withCoreCards(layout: ProfileLayout, boardW: number): ProfileLayout {
  const missing = CORE.filter((panel) => !layout.cards[panel.id]);
  if (!missing.length) return layout;
  const cards = { ...layout.cards };
  const width = Math.max(boardW, 1);
  for (const panel of missing) {
    const taken = Object.values(cards)
      .filter((card) => card.shown)
      .map((card) => cardPixels(card, width));
    cards[panel.id] = pixelsToCard(findSlot(width, taken, panel.size), width, true);
  }
  return { ...layout, cards };
}

function plainLayout(layout: ProfileLayout): ProfileLayout {
  // The screen keeps the layout in a Vue ref, so a nested card can be a proxy. JSON strips that.
  return JSON.parse(JSON.stringify(layout)) as ProfileLayout;
}

function touch(layout: ProfileLayout): ProfileLayout {
  return { ...layout, sync: 'pending', rev: layout.rev + 1 };
}

export function moveCard(layout: ProfileLayout, id: string, px: StatPixels, boardW: number): ProfileLayout {
  const shown = layout.cards[id]?.shown !== false;
  return { ...layout, cards: { ...layout.cards, [id]: pixelsToCard(px, boardW, shown) } };
}

export function hideCard(layout: ProfileLayout, id: string): ProfileLayout {
  const card = layout.cards[id];
  if (!card?.shown) return layout;
  return touch({ ...layout, cards: { ...layout.cards, [id]: { ...card, shown: false } } });
}

function overlaps(a: StatPixels, b: StatPixels): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function findSlot(boardW: number, taken: StatPixels[], size?: { w: number; h: number }): StatPixels {
  const width = Math.max(boardW, 1);
  const w = Math.min(width, Math.max(STAT_MIN_W, size?.w ?? 148));
  const h = Math.min(PROFILE_BOARD_MAX_H, Math.max(STAT_MIN_H, size?.h ?? STAT_ROW));
  for (let y = 0; y + h <= PROFILE_BOARD_MAX_H + 0.01; y += STAT_GAP) {
    for (let x = 0; x + w <= width + 0.01; x += STAT_GAP) {
      const box = { x, y, w, h };
      if (!taken.some((t) => overlaps(box, t))) return box;
    }
  }
  return { x: 0, y: 0, w, h };
}

export function showCard(layout: ProfileLayout, id: string, boardW: number): ProfileLayout {
  const existing = layout.cards[id];
  if (existing?.shown) return layout;
  const cards = { ...layout.cards };
  if (existing) cards[id] = clampFrac({ ...existing, shown: true });
  else {
    const taken = Object.values(cards)
      .filter((card) => card.shown)
      .map((card) => cardPixels(card, boardW));
    cards[id] = pixelsToCard(findSlot(boardW, taken, profilePanels().find((p) => p.id === id)?.size), boardW, true);
  }
  return touch({ ...layout, cards });
}

export function markLayoutPending(layout: ProfileLayout): ProfileLayout {
  return touch(layout);
}

/** The saved layout, or null when the person has never moved a card. */
export function readProfileLayout(): ProfileLayout | null {
  let raw: unknown;
  try {
    raw = JSON.parse(profileLocal.getItem(PROFILE_LAYOUT_KEY) ?? 'null');
  } catch {
    saved = null;
    return null;
  }
  const layout = parse(raw);
  saved = layout ? plainLayout(layout) : null;
  return layout ? plainLayout(layout) : null;
}

export function writeProfileLayout(layout: ProfileLayout): void {
  saved = plainLayout(layout);
  try {
    profileLocal.setItem(PROFILE_LAYOUT_KEY, JSON.stringify(saved));
  } catch {
    /* storage full or blocked: this visit still shows the layout */
  }
}

/** Future cloud sync reads this when the tray changed here. Null means nothing is waiting. */
export function pendingProfileLayoutSync(): ProfileLayout | null {
  if (!saved) readProfileLayout();
  return saved?.sync === 'pending' ? plainLayout(saved) : null;
}

/** A future sync calls this after the cloud copy matches this layout. */
export function ackProfileLayoutSync(): void {
  if (!saved || saved.sync === 'acked') return;
  writeProfileLayout({ ...saved, sync: 'acked' });
}

/** Future sync replaces the layout with the cloud copy. No-op until one is connected. */
export const profileLayoutSync: { pull(): Promise<ProfileLayout | null> } = {
  pull: () => Promise.resolve(null),
};
